<template>
    <v-menu
        v-model="open"
        :close-on-content-click="false"
        location="bottom end"
        offset="8"
        max-width="380"
    >
        <template #activator="{ props }">
            <v-btn
                v-bind="props"
                icon
                variant="text"
                size="small"
                :aria-label="$t('notifications.bell-label')"
            >
                <v-badge
                    v-if="notificationStore.unreadCount > 0"
                    :content="String(notificationStore.unreadCount)"
                    color="error"
                    floating
                    offset-x="-2"
                    offset-y="-2"
                >
                    <v-icon>mdi-bell</v-icon>
                </v-badge>
                <v-icon v-else>mdi-bell-outline</v-icon>
            </v-btn>
        </template>

        <v-card>
            <v-card-title class="text-subtitle-1 font-weight-medium">
                {{ $t('notifications.title') }}
                <span v-if="notificationStore.totalCount > 0" class="text-caption text-medium-emphasis ml-2">
                    ({{ notificationStore.totalCount }})
                </span>
            </v-card-title>

            <v-divider />

            <v-list v-if="notificationStore.items.length > 0" lines="two" max-height="400" class="overflow-y-auto">
                <v-list-item
                    v-for="notif in notificationStore.items"
                    :key="notif.id"
                    :class="{ 'notification-unread': isUnread(notif) }"
                    @click="handleClick(notif)"
                >
                    <template #prepend>
                        <v-icon :color="iconColor(notif.type)">{{ iconName(notif.type) }}</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ notif.title || $t('notifications.untitled') }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                        {{ notif.message }}
                    </v-list-item-subtitle>
                </v-list-item>
            </v-list>

            <div v-else class="text-center text-medium-emphasis pa-6 text-body-2">
                {{ $t('notifications.empty') }}
            </div>
        </v-card>
    </v-menu>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';

const open = ref(false);
const notificationStore = useNotificationStore();

onMounted(() => {
    notificationStore.startPolling();
});

onUnmounted(() => {
    notificationStore.stopPolling();
});

const isUnread = (n: Notification) => n.source === 'stored' && !n.readAt;

const iconName = (type: string): string => {
    switch (type) {
        case 'error':
            return 'mdi-alert-circle';
        case 'warning':
            return 'mdi-alert';
        case 'calibration_assigned':
            return 'mdi-clipboard-text';
        case 'master_cert_pending_approval':
        case 'master_cert_under_technical_review':
            return 'mdi-check-decagram';
        case 'master_cert_approved':
            return 'mdi-check-circle';
        case 'master_cert_rejected':
            return 'mdi-close-circle';
        case 'chat_message_from_customer':
            return 'mdi-message';
        case 'acclimatization_expired':
            return 'mdi-timer-sand-complete';
        default:
            return 'mdi-information';
    }
};

const iconColor = (type: string): string => {
    switch (type) {
        case 'error':
        case 'master_cert_rejected':
            return 'error';
        case 'warning':
            return 'warning';
        case 'master_cert_approved':
            return 'success';
        default:
            return 'primary';
    }
};

const handleClick = (notif: Notification) => {
    if (isUnread(notif)) {
        void notificationStore.markRead(notif.id);
    }
    // Zatím bez deep-link routingu podle notif.metadata.deepLink.
};
</script>

<style scoped>
.notification-unread {
    background-color: rgba(33, 150, 243, 0.05);
}
</style>
